import { useState, useEffect } from 'react';
import { useApp } from '../context/AppContext';
import { api } from '../lib/api';
import { tierInfo } from '../data/mockData';
import { SongCard } from '../components/SongCard';
import type { Song } from '../types';

export function ProfilePage() {
  const { currentUser, logout, theme, toggleTheme, refreshUser } = useApp();
  const [bio, setBio] = useState(currentUser?.bio ?? '');
  const [artistName, setArtistName] = useState(currentUser?.artistName ?? '');
  const [mySongs, setMySongs] = useState<Song[]>([]);
  const [loadingSongs, setLoadingSongs] = useState(true);
  const [saving, setSaving] = useState(false);
  const [profileMsg, setProfileMsg] = useState<string | null>(null);
  const [profileErr, setProfileErr] = useState<string | null>(null);

  const [securityEmail,setSecurityEmail] = useState(currentUser?.email || '');
  const [currentPassword,setCurrentPassword] = useState('');
  const [newPassword,setNewPassword] = useState('');
  const [securityMsg,setSecurityMsg] = useState('');
  const updateSecurity = async () => {
    setSaving(true); setSecurityMsg('');
    try { await api.updateSecurity({email:securityEmail,currentPassword,newPassword}); await logout(); }
    catch(e:any) { setSecurityMsg(e.message || 'Could not update security.'); }
    finally { setSaving(false); }
  };
  const tier = tierInfo.find((t) => t.id === currentUser?.tier) ?? tierInfo[0];
  const isMinor = currentUser?.ageGroup === 'under13' || (currentUser?.ageGroup === '13to17' && !currentUser?.ageVerified);

  useEffect(() => {
    api.mySongs().then((d) => { setMySongs(d.songs || []); setLoadingSongs(false); }).catch(() => setLoadingSongs(false));
  }, []);

  const saveProfile = async () => {
    setSaving(true); setProfileMsg(null); setProfileErr(null);
    try {
      const { user } = await api.updateProfile({ bio, artistName });
      refreshUser?.(user);
      setProfileMsg('Profile updated.');
    } catch (e: any) {
      setProfileErr(e?.detail?.message || e?.message || 'Could not save profile.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="grid cols-2">
      <div className="card">
        <div className="flex gap-12 mb-16" style={{ alignItems: 'center' }}>
          <div className="avatar" style={{ background: currentUser?.avatarColor, width: 56, height: 56, fontSize: 20 }}>
            {(currentUser?.username ?? 'U').slice(0, 2).toUpperCase()}
          </div>
          <div>
            <h2 style={{ margin: 0 }}>{currentUser?.artistName || currentUser?.username}</h2>
            <div className="muted" style={{ fontSize: 13 }}>{currentUser?.email}</div>
            {currentUser?.artistName && (
              <div className="muted" style={{ fontSize: 11 }}>@{currentUser?.username}</div>
            )}
          </div>
        </div>
        <div className="flex gap-8 wrap mb-16">
          <span className="chip chip-gold">{currentUser?.role}</span>
          <span className="chip chip-approved">{tier.name} plan</span>
          {currentUser?.tierExpiresAt && (
            <span className="chip chip-private">Tier expires {currentUser.tierExpiresAt}</span>
          )}
        </div>
        <div className="form-group">
          <label className="label">Artist name</label>
          <input className="input" value={artistName} placeholder="Your stage name (leave blank to use @username)" maxLength={40} onChange={(e) => setArtistName(e.target.value)} />
          <div className="muted" style={{ fontSize: 11, marginTop: 4 }}>This is your public name across IAMMUZIK — battles, the Wave, your profile.</div>
        </div>
        <div className="form-group">
          <label className="label">Bio</label>
          <textarea className="textarea" value={bio} maxLength={500} onChange={(e) => setBio(e.target.value)} />
          <div className="muted" style={{ fontSize: 11, marginTop: 4 }}>{bio.length}/500</div>
          <div className="flex gap-8 wrap" style={{ marginTop: 10, alignItems: 'center' }}>
            <button className="btn btn-primary btn-sm" onClick={saveProfile} disabled={saving}>{saving ? 'Saving…' : 'Save profile'}</button>
            {profileMsg && <span className="chip chip-approved" style={{ fontSize: 11 }}>{profileMsg}</span>}
            {profileErr && <span className="chip chip-explicit" style={{ fontSize: 11 }}>{profileErr}</span>}
          </div>
        </div>
      </div>

      <div className="grid">
        <div className="card">
          <div className="section-title">Account & Security</div>
          <div className="form-group">
            <label className="label">Email</label>
            <input className="input" type="email" aria-label="Account email" value={securityEmail} onChange={e=>setSecurityEmail(e.target.value)} />
          </div>
          <div className="form-group">
            <label className="label">Current password</label>
            <input className="input" type="password" autoComplete="current-password" aria-label="Current password" value={currentPassword} onChange={e=>setCurrentPassword(e.target.value)} />
            <label className="label">New password (optional)</label>
            <input className="input" type="password" autoComplete="new-password" aria-label="New password" value={newPassword} onChange={e=>setNewPassword(e.target.value)} />
          </div>
          <button className="btn btn-outline btn-sm" disabled={saving || !currentPassword || !securityEmail} onClick={updateSecurity}>Update security</button>
          <p className="field-hint">Updating security signs you out on all devices. Sign in again with your updated details.</p>
          {securityMsg && <p role="alert">{securityMsg}</p>}
        </div>

        <div className="card">
          <div className="section-title">Preferences</div>
          <div className="flex-between mb-16">
            <span>Theme</span>
            <button className="btn btn-outline btn-sm" onClick={toggleTheme}>
              {theme === 'dark' ? '☀ Switch to Light' : '☾ Switch to Dark'}
            </button>
          </div>

        </div>

        <div className="card">
          <div className="section-title">Subscription</div>
          <div className="muted" style={{ fontSize: 14 }}>
            Current plan: <strong>{tier.name}</strong> ({tier.price}/mo). Real
            subscription management via Stripe arrives in Phase 4.
          </div>
        </div>

        <button className="btn btn-danger btn-block" onClick={logout}>
          Logout
        </button>
      </div>

      {/* My Library — shows user's songs including explicit versions */}
      <div className="card" style={{ gridColumn: '1 / -1' }}>
        <div className="section-title">My Library</div>
        <div className="muted" style={{ fontSize: 13, marginBottom: 12 }}>
          {isMinor
            ? 'You are under 18. Explicit songs in your library are only visible to you. Other users cannot see your explicit songs.'
            : 'Your explicit songs are visible to users 18+ who visit your profile. On The Wave, explicit lyrics are censored with [BEEP] — like TV or radio. The uncensored versions are available here.'}
        </div>
        {loadingSongs ? (
          <div className="muted">Loading your songs...</div>
        ) : mySongs.length === 0 ? (
          <div className="empty">No songs yet. Go to Studio to create one.</div>
        ) : (
          <div className="grid cols-2">
            {mySongs.map((song) => (
              <SongCard key={song.id} song={song}>
                <div className="flex gap-8 wrap">
                  <span className={`chip chip-${song.contentRating === 'EXPLICIT' ? 'denied' : 'approved'}`} style={{ fontSize: 11 }}>
                    {song.contentRating === 'EXPLICIT' ? '⚠ Explicit' : '✓ Clean'}
                  </span>
                  <span className={`chip chip-${song.privacy === 'PUBLIC_APPROVED' ? 'approved' : 'private'}`} style={{ fontSize: 11 }}>
                    {song.privacy === 'PUBLIC_APPROVED' ? 'On The Wave' : 'Private'}
                  </span>
                </div>
              </SongCard>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
